/**
 * velos-vus-recemment.js
 *
 * Rend la rangée « Vous avez consulté récemment » à partir de l'historique local
 * (localStorage `mint:velos-vus`, écrit par snippets/velos-vus-recemment-record.liquid).
 *
 * Les cartes sont rendues PAR LE SERVEUR : un seul fetch vers
 *   /search?q=id:A OR id:B…&type=product&options[unavailable_products]=show&section_id=<section>
 * puis DOMParser + remplacement atomique du <product-carousel>.
 * C'est le pattern déjà utilisé par theme.Recommendations (assets/theme.js:4665-4700).
 *
 * `options[unavailable_products]=show` est OBLIGATOIRE : sans lui, un vélo vendu
 * disparaît silencieusement de la réponse (mesuré le 2026-09-18).
 *
 * Chargé en defer par sections/velos-vus-recemment.liquid.
 */
(function () {
  'use strict';

  var HISTORY_KEY = 'mint:velos-vus';
  var RESULTS_KEY = 'mint:derniere-recherche';
  var OBSERVER_MARGIN = '600px';
  // Nombre de reponses consecutives sans un id avant de le retirer de l'historique.
  // 1 confondait « produit depublie » et « pas encore dans l'index de recherche ».
  var MISSING_TOLERANCE = 3;

  function lsGet(key) {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function lsSet(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      /* quota / navigation privée : silencieux */
    }
  }

  function lsRemove(key) {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      /* silencieux */
    }
  }

  /** Historique nettoyé : entiers positifs uniquement, ordre de récence préservé. */
  function readHistory() {
    var raw = lsGet(HISTORY_KEY);
    if (!raw) return [];
    var list;
    try {
      list = JSON.parse(raw);
    } catch (e) {
      return [];
    }
    if (!Array.isArray(list)) return [];
    return list.filter(function (o) {
      return o && Number.isInteger(o.i) && o.i > 0;
    });
  }

  function writeHistory(list) {
    if (list.length === 0) {
      lsRemove(HISTORY_KEY);
      return;
    }
    lsSet(HISTORY_KEY, JSON.stringify(list));
  }

  /**
   * Cible de « Reprendre ma sélection ».
   * 1. dernière page de résultats filtrée (miroir localStorage de back-to-results) ;
   * 2. repli : collection d'origine du vélo le plus récemment vu ;
   * 3. sinon : null, le lien reste masqué.
   * La valeur est RE-VALIDÉE ici : same-origin, chemin /collections ou /search.
   */
  function resumeTarget(history) {
    // Pas de `label` ici (finding M7) : `setupResumeLink()` n'utilise que `href`, le
    // libellé du lien vient du setting de section ou de la locale. Lire et transporter
    // `mint:derniere-recherche-libelle` était du code mort dès l'écriture (clean-code n°9).
    var stored = lsGet(RESULTS_KEY);
    var validated = validateTarget(stored);
    if (validated) {
      return { href: validated };
    }
    var first = history[0];
    if (first && typeof first.c === 'string' && first.c !== '') {
      var handle = first.c.replace(/[^a-z0-9-]/gi, '');
      if (handle !== '') {
        return { href: root() + 'collections/' + handle };
      }
    }
    return null;
  }

  function validateTarget(raw) {
    if (!raw) return null;
    try {
      var u = new URL(raw, location.origin);
      if (u.origin !== location.origin) return null;
      var p = u.pathname;
      var okCollections = p === '/collections' || p.indexOf('/collections/') === 0;
      var okSearch = p === '/search' || p.indexOf('/search/') === 0;
      if (okCollections === false && okSearch === false) return null;
      // Les parametres de parcours FMD sont RETIRES de la cible. `storeOrigin()`
      // memorise `pathname + search` sans filtre : une URL `?fmd=<handle>` visitee avant
      // la sortie du parcours reste en stockage, et la sortie FMD ne nettoie que `fmd`,
      // `fmd-data` et `fmd-generic-flow`. Sans ce retrait, « Reprendre ma selection »
      // remet le visiteur en tarification partenaire — et `header-fmd.liquid` reecrit
      // alors `localStorage['fmd']` : le parcours se rouvre seul. Le nettoyage est fait
      // ICI et pas seulement a l'ecriture, pour couvrir les URL deja stockees.
      u.searchParams.delete('fmd');
      u.searchParams.delete('fmd-generic');
      u.searchParams.delete('fmd-data');
      // `view` seulement sur `parcours-fmd` : le theme s'en sert aussi pour `finder`,
      // `location`, `quick-preview`, `ajax`. Retirer la cle entiere renverrait le
      // visiteur sur la vue par defaut de la collection d'ou il vient.
      if (u.searchParams.get('view') === 'parcours-fmd') u.searchParams.delete('view');
      return u.pathname + u.search;
    } catch (e) {
      return null;
    }
  }

  function root() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  function track(name, payload) {
    if (typeof window.trackEvent === 'function') {
      window.trackEvent(name, payload);
      return;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: name }, payload));
  }

  function VelosVusRecemment(root_) {
    this.root = root_;
    this.sectionId = root_.getAttribute('data-vvr-section-id') || '';
    this.limit = parseInt(root_.getAttribute('data-vvr-limit'), 10) || 8;
    this.carousel = root_.querySelector('[data-vvr-carousel]');
    this.link = root_.querySelector('[data-vvr-link]');
    this.loaded = false;
    this.viewSent = '';
    // Sur une fiche produit, le produit courant est retire de SA PROPRE liste : le
    // snippet d'enregistrement l'a empile en tete juste avant, et il n'a rien a faire
    // dans « vous avez consulte recemment » de la page qui l'affiche. Filtre a la
    // source pour que tout en decoule : nombre de squelettes, ids demandes, cible de
    // « Reprendre ma selection ». `purge()` relit le stockage complet, donc l'id exclu
    // reste enregistre pour les autres pages.
    this.excludeId = parseInt(root_.getAttribute('data-vvr-exclude'), 10);
    var excludeId = this.excludeId;
    this.history = readHistory().filter(function (o) {
      return o.i !== excludeId;
    });

    if (this.history.length === 0 || !this.carousel || this.sectionId === '') {
      this.root.hidden = true;
      return;
    }

    // Demasquage par le MODULE, en plus du script inline pre-paint.
    // Mesure du 2026-09-21 : `root.hidden = false` n'existait qu'en un seul point du
    // theme, dans ce script inline — or l'editeur reinsere le HTML de la section sans
    // re-executer ses <script>. Simule (remplacement du noeud par le HTML de
    // ?sections=<id> puis `shopify:section:load` emis sur la section) : le bloc passait
    // de 522px / hidden:false a 0px / hidden:true, 8 squelettes, 0 carte, et n'en
    // revenait plus. Le marchand ne pouvait donc valider aucun reglage.
    // L'inline garde son role : decider AVANT le premier paint (anti-CLS). Le module ne
    // fait que rattraper les chemins ou il n'a pas tourne.
    this.root.hidden = false;

    this.setupResumeLink();
    this.observe();
  }

  VelosVusRecemment.prototype.setupResumeLink = function () {
    if (!this.link) return;
    var target = resumeTarget(this.history);
    if (!target) {
      this.link.hidden = true;
      return;
    }
    this.link.setAttribute('href', target.href);
    this.link.hidden = false;
  };

  VelosVusRecemment.prototype.observe = function () {
    var self = this;
    if (typeof IntersectionObserver !== 'function') {
      this.load();
      return;
    }
    this.observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          self.disconnect();
          self.load();
        });
      },
      { rootMargin: OBSERVER_MARGIN },
    );
    this.observer.observe(this.root);
  };

  /**
   * Repli sur echec. Le bloc a deja ete demasque par le script pre-paint avec ses
   * squelettes (~500px) : le masquer alors qu'il est DANS le viewport reproduit
   * exactement le CLS que `holdHeight()` combat — et l'observer de chargement se
   * declenchant 600px avant, sur reseau lent l'echec tombe souvent quand le bloc est
   * deja a l'ecran. On ne masque donc que s'il n'est pas visible.
   */
  VelosVusRecemment.prototype.failClosed = function () {
    var r = this.root.getBoundingClientRect();
    var visible = r.bottom > 0 && r.top < (window.innerHeight || 0);
    if (visible) {
      this.root.setAttribute('data-vvr-failed', 'true');
      return;
    }
    this.root.hidden = true;
  };

  VelosVusRecemment.prototype.disconnect = function () {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.impressionObserver) {
      this.impressionObserver.disconnect();
      this.impressionObserver = null;
    }
  };

  /*
   * URL construite À LA MAIN, pas avec URLSearchParams (finding M1).
   * `params.set('options[unavailable_products]', …)` sérialise la clé en
   * `options%5Bunavailable_products%5D` : TOUTES les mesures de la phase 0ter et
   * TOUTES les portes de vérification (T10, T19) utilisent des crochets LITTÉRAUX.
   * Si Shopify n'acceptait pas la forme encodée, le paramètre serait ignoré, les vélos
   * vendus disparaîtraient de la réponse et la branche « badge Vendu / Me prévenir »
   * deviendrait morte — sans aucune erreur. On émet donc exactement ce qui a été mesuré.
   */
  VelosVusRecemment.prototype.requestUrl = function (ids) {
    var sorted = ids.slice().sort(function (a, b) {
      return a - b;
    });
    var q = sorted
      .map(function (id) {
        return 'id:' + id;
      })
      .join(' OR ');
    return (
      root() +
      'search?q=' +
      encodeURIComponent(q) +
      '&type=product' +
      '&options[unavailable_products]=show' +
      '&section_id=' +
      encodeURIComponent(this.sectionId)
    );
  };

  VelosVusRecemment.prototype.load = function () {
    if (this.loaded) return;
    this.loaded = true;

    var self = this;
    var ids = this.history.slice(0, this.limit).map(function (o) {
      return o.i;
    });

    this.carousel.setAttribute('aria-busy', 'true');

    fetch(this.requestUrl(ids))
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        self.render(html, ids);
      })
      .catch(function (error) {
        if (window.console && console.warn) {
          console.warn('[velos-vus-recemment] rendu impossible :', error);
        }
        self.failClosed();
      });
  };

  VelosVusRecemment.prototype.render = function (html, requestedIds) {
    var self_ = this;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fetched = doc.querySelector('[data-vvr-carousel]');
    if (!fetched) {
      this.failClosed();
      return;
    }

    var track_ = fetched.querySelector('[data-vvr-track]');
    if (!track_) {
      this.failClosed();
      return;
    }

    // Réordonnancement : l'ordre serveur ne suit pas l'ordre demandé (mesuré).
    var slides = Array.prototype.slice.call(track_.querySelectorAll('[data-vvr-slide]'));
    var byId = {};
    slides.forEach(function (slide) {
      var id = parseInt(slide.getAttribute('data-vvr-slide'), 10);
      if (Number.isInteger(id)) byId[id] = slide;
    });

    var ordered = [];
    var missing = [];
    requestedIds.forEach(function (id) {
      if (byId[id]) {
        ordered.push(byId[id]);
      } else {
        missing.push(id);
      }
    });

    // Aucune carte revenue : le serveur ne s'est PAS prononce sur la disparition
    // des produits, il n'a rien renvoye du tout (index de recherche froid apres
    // un import, produits fraichement republies, panne du provider). Purger ici
    // effacerait TOUT l'historique du visiteur sur un faux negatif, sans retour
    // possible. On masque et on retentera au prochain chargement.
    if (ordered.length === 0) {
      this.failClosed();
      return;
    }

    while (track_.firstChild) {
      track_.removeChild(track_.firstChild);
    }
    ordered.forEach(function (slide) {
      track_.appendChild(slide);
    });

    // La section fetchée a reçu les DÉFAUTS du schema, pas les réglages du
    // marchand (limite de l'API Section Rendering) : on recopie la
    // configuration et le nom accessible depuis le carrousel de la page.
    // `data-show-arrows` / `data-show-dots` ne sont JAMAIS lus par product-carousel.js
    // (il fait querySelector sur les classes des flèches, :176 et :196) : les recopier
    // ne servait à rien. En revanche `data-autoplay` et `data-autoplay-speed` le sont
    // (:132, :141). Finding H4.
    ['data-products-per-slide', 'data-autoplay', 'data-autoplay-speed', 'data-wrap-around', 'aria-label', 'role'].forEach(
      function (attr) {
        var value = self_.carousel.getAttribute(attr);
        if (value !== null) fetched.setAttribute(attr, value);
      },
    );

    // La branche fetchée rend la navigation inconditionnellement (elle ne reçoit que les
    // DÉFAUTS du schema, pas le réglage `show_arrows` du marchand). On aligne donc le
    // carrousel fetché sur celui de la page : si la page n'a pas de flèches, on les
    // retire. Finding H4 — sans ce couple (rendu dans les 2 branches + alignement ici),
    // le replaceChild détruisait les flèches à tous les coups.
    var pageNav = self_.carousel.querySelector('.product-carousel__navigation');
    var fetchedNav = fetched.querySelector('.product-carousel__navigation');
    if (!pageNav && fetchedNav) {
      fetchedNav.parentNode.removeChild(fetchedNav);
    }

    // Flickity a pu s'initialiser sur les squelettes : on le détruit
    // explicitement avant de détacher le nœud. `destroy()` est idempotent.
    if (typeof this.carousel.destroy === 'function') {
      this.carousel.destroy();
    }

    // Hauteur RÉSERVÉE pendant la bascule. `connectedCallback` de product-carousel.js
    // appelle `init()`, qui attend `waitForFlickity()` — une Promise, donc jamais dans
    // la même tâche que le replaceChild ci-dessous. Entre les deux, le carrousel fetché
    // est dans le DOM sans viewport dimensionné : le bloc retombe à la hauteur de son
    // seul en-tête et tout ce qui suit remonte, puis redescend.
    // Mesuré le 2026-09-21 (390px, CPU 4x, Slow 4G) : hauteur du bloc 497 -> 72 -> 498,
    // soit un CLS de 0,3867 attribué aux 5 sections situées sous le bloc.
    var reservedHeight = Math.round(this.carousel.getBoundingClientRect().height);

    fetched.removeAttribute('aria-busy');
    this.carousel.parentNode.replaceChild(fetched, this.carousel);
    this.carousel = fetched;
    this.holdHeight(reservedHeight);

    // Le CTA d'alerte est rendu par le Liquid en FRÈRE de la carte (product-grid-item
    // ne laisse aucun point d'insertion). On le pose SUR la photo pour qu'il cesse
    // d'allonger le slide — ce qui supprime aussi les ~40px de saut résiduel au moment
    // où les cartes remplacent les squelettes.
    //
    // Il va dans `.grid-product__content` (FRÈRE du <a>), jamais dans
    // `.grid-product__image-mask` qui, elle, est DANS le <a class="grid-product__link">.
    // Un <button> dans un <a> est du contenu interactif imbriqué : le lecteur d'écran
    // annonce « … Me prévenir, lien » puis le bouton une seconde fois, et sur 8 cartes
    // la navigation par liens devient illisible.
    //
    // Le calage vertical passe par un slot au ratio du média (100/66, la règle
    // `.grid__image-ratio--landscape` du thème, déjà reprise par `.vvr__skeleton-media`) :
    // un padding en pourcentage se résout sur la LARGEUR du conteneur, seule façon de
    // reproduire la hauteur de la photo en CSS pur, sans mesure ni ResizeObserver.
    Array.prototype.forEach.call(fetched.querySelectorAll('.vvr-sold__cta'), function (cta) {
      var slide = cta.closest('[data-vvr-slide]');
      var content = slide && slide.querySelector('.grid-product__content');
      if (!content) return;
      var slot = document.createElement('div');
      slot.className = 'vvr-sold__slot';
      slot.appendChild(cta);
      content.appendChild(slot);
    });

    if (typeof theme !== 'undefined' && typeof theme.reinitProductGridItem === 'function') {
      theme.reinitProductGridItem(this.root);
    }

    this.purge(missing);
    // L'impression se compte quand le bloc est VU, pas quand il est charge : l'observer
    // de chargement se declenche 600px avant, donc un visiteur qui s'arrete au-dessus du
    // bloc gonflait le denominateur du CTR — precisement la metrique qui justifie le
    // composant. Le verrou de signature rend l'appel idempotent.
    this.ordered = ordered;
    this.observeImpression();
  };

  /**
   * Maintient la hauteur du carrousel tant que Flickity n'a pas dimensionné son viewport.
   * Relâchée dès que le viewport a une hauteur réelle, et de toute façon au bout de
   * ~5 s : un plancher oublié figerait la carte si le contenu devenait plus court
   * (rotation d'écran, resize).
   */
  VelosVusRecemment.prototype.holdHeight = function (px) {
    if (!px || typeof requestAnimationFrame !== 'function') return;
    var node = this.carousel;
    // `min-height` ne s'applique pas a un inline non remplace, et <product-carousel> est
    // un element custom, donc `display: inline` par defaut. Mesure : sans mise en bloc, la
    // reserve est purement decorative et le bloc retombe a 72px (CLS 0,50 au lieu de
    // 0,39 — elle ne faisait que retarder le saut). Le `display: block` vit desormais
    // dans la feuille (`.vvr__carousel`), en regime PERMANENT : le poser ici en style
    // inline temporaire laissait l'element revenir a `inline` entre deux reserves, donc
    // le piege intact pour la prochaine.
    node.style.minHeight = px + 'px';
    // Borne en TEMPS, pas en frames : 300 frames valent 2,5 s a 120 Hz et l'infini dans
    // un onglet d'arriere-plan, ou `requestAnimationFrame` ne tourne pas.
    var deadline = Date.now() + 5000;
    function release() {
      // `.product-carousel__viewport` n'existe nulle part dans le theme : Flickity nomme
      // son conteneur `.flickity-viewport`.
      var viewport = node.querySelector('.flickity-viewport');
      var h = viewport ? viewport.getBoundingClientRect().height : 0;
      if (h > 0 || Date.now() > deadline) {
        node.style.minHeight = '';
        return;
      }
      requestAnimationFrame(release);
    }
    requestAnimationFrame(release);
  };

  /**
   * Retire de l'historique les produits que le serveur n'a pas servis — mais seulement
   * apres MISSING_TOLERANCE reponses consecutives sans eux.
   *
   * Effacer des la premiere absence confondait « produit depublie » et « pas encore
   * dans l'index de recherche ». Un import nocturne ou une republication suffisait a
   * faire disparaitre des velos vivants de l'historique du visiteur, definitivement et
   * sans retour possible. Le compteur `m` vit dans l'entree ; il est remis a zero des
   * que le produit revient, et les entrees ecrites avant cette version (sans `m`)
   * repartent naturellement de 0.
   */
  VelosVusRecemment.prototype.purge = function (missingIds) {
    if (missingIds.length === 0) return;
    var list = readHistory()
      .map(function (o) {
        if (missingIds.indexOf(o.i) === -1) {
          delete o.m;
          return o;
        }
        o.m = (o.m || 0) + 1;
        return o;
      })
      .filter(function (o) {
        return (o.m || 0) < MISSING_TOLERANCE;
      });
    writeHistory(list);
    this.history = list.filter(function (o) {
      return o.i !== this.excludeId;
    }, this);
  };

  /*
   * Ni ouverture de modale ni click_cta ici :
   * - la modale d'alerte s'ouvre par la classe `x-button__alert-modal` posée
   *   dans le markup (x-modal.js:52-60 écoute au niveau document). Un .open()
   *   direct perdrait le tracking `start_bike_finder` ;
   * - `click_cta` exige un listener DÉLÉGUÉ dédié : tracking-ui-forms.liquid:198
   *   fait `if (ctaElement.closest('[data-cta-name]')) return;`, donc un CTA qui
   *   porte data-cta-name sans listener propre n'émet RIEN. Voir T27.
   */

  /** Même extraction de prix que select_item (tracking-ecommerce.liquid). */
  function priceOf(card) {
    var el = card.querySelector('.grid-product__price-final');
    if (!el) return 0;
    var raw = (el.innerText || el.textContent || '').replace(/[^0-9,.]/g, '').replace(',', '.');
    var value = parseFloat(raw);
    return isNaN(value) ? 0 : value;
  }

  /** La marque est le premier nœud texte du titre, comme dans select_item. */
  function brandOf(card) {
    var title = card.querySelector('.grid-product__title');
    if (!title || !title.childNodes.length) return '';
    return (title.childNodes[0].textContent || '').trim();
  }

  /** Second observer, au ras du viewport : il ne sert QU'A compter l'impression. */
  VelosVusRecemment.prototype.observeImpression = function () {
    var self = this;
    if (typeof IntersectionObserver !== 'function') {
      self.sendViewItemList(self.ordered);
      return;
    }
    this.impressionObserver = new IntersectionObserver(
      function (entries) {
        if (!entries[0] || !entries[0].isIntersecting) return;
        self.sendViewItemList(self.ordered);
        if (self.impressionObserver) {
          self.impressionObserver.disconnect();
          self.impressionObserver = null;
        }
      },
      { rootMargin: '0px', threshold: 0.3 },
    );
    this.impressionObserver.observe(this.root);
  };

  VelosVusRecemment.prototype.sendViewItemList = function (slides) {
    var items = slides
      .map(function (slide, index) {
        var card = slide.querySelector('[data-product-id]');
        if (!card) return null;
        var title = card.querySelector('.grid-product__title');
        return {
          item_id: card.getAttribute('data-product-id') || '',
          item_name: title ? title.textContent.replace(/\s+/g, ' ').trim() : '',
          item_brand: brandOf(card),
          item_category: card.getAttribute('data-product-category') || '',
          item_variant: card.getAttribute('data-product-variant') || '',
          price: priceOf(card),
          index: index + 1,
        };
      })
      .filter(Boolean);

    if (items.length === 0) return;

    // Verrou anti-double-push : même signature = même impression.
    var signature = items
      .map(function (i) {
        return i.item_id;
      })
      .join('|');
    if (signature === this.viewSent) return;
    this.viewSent = signature;

    // item_list_name doit valoir le TEXTE DU H2, pas un slug : select_item ne
    // porte pas d'item_list_id, il porte `carrousel_name` = texte du h2
    // (tracking-ecommerce.liquid:1186-1192). Sans cet alignement, le CTR de la
    // liste ne se calcule pas — c'est pourtant la justification du composant.
    var heading = this.root.querySelector('.vvr__title');
    var listName = heading ? heading.textContent.trim() : 'recently_viewed';

    track('view_item_list', {
      ecommerce: {
        item_list_id: 'recently_viewed',
        item_list_name: listName,
        items: items,
      },
    });
  };

  VelosVusRecemment.prototype.destroy = function () {
    this.disconnect();
  };

  var instances = [];

  /*
   * Garde FMD, doublé ici (finding N15). Le script inline pré-paint de la section porte
   * déjà les mêmes 4 tests, et c'est lui qui décide de l'affichage — mais ce module est
   * chargé indépendamment et armerait sinon son IntersectionObserver en contexte FMD.
   * Inoffensif tant que la racine reste `hidden`, et c'est précisément ce genre de
   * « inoffensif tant que » qui casse au premier refactoring.
   */
  function fmdContext() {
    try {
      var u = new URL(window.location.href);
      if (u.searchParams.has('fmd')) return true;
      if (u.searchParams.has('fmd-generic')) return true;
      if (localStorage.getItem('fmd')) return true;
      if (localStorage.getItem('fmd-generic-flow') === 'true') return true;
      return false;
    } catch (e) {
      return true;
    }
  }

  function init(scope) {
    if (fmdContext()) return;
    var roots = (scope || document).querySelectorAll('[data-vvr]:not([data-vvr-ready])');
    Array.prototype.forEach.call(roots, function (el) {
      el.setAttribute('data-vvr-ready', 'true');
      instances.push(new VelosVusRecemment(el));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Éditeur de thème : le HTML est remplacé sans rechargement, le JS de chargement
  // ne se rejoue pas tout seul.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    instances = instances.filter(function (instance) {
      if (event.target.contains(instance.root)) {
        instance.destroy();
        return false;
      }
      return true;
    });
  });
})();
